import { useEffect, useState, useRef } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { TransitionGroup } from 'react-transition-group'
import Collapse from '@mui/material/Collapse'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import useQualtricsSurvey from '../hooks/useQualtricsSurvey'
import useContentful from '../contexts/useContentful'

let timeout = 13_500_000
let promptBeforeIdle = 11_700_000

// Get the query parameter from the URL
const queryParams = new URLSearchParams(window.location.search)
const idletime = queryParams.get('idletime')

if (idletime !== null) {
  timeout = idletime * 1000 + 5000
  promptBeforeIdle = idletime * 1000
}

export default function TimeoutDialog() {
  // Get content from Contentful
  const {
    stillThereDialogTitle,
    stillThereMessage,
    stillThereButtonText,
    sessionExpiredDialogTitle,
    sessionExpiredMessage,
    sessionExpireButtonText,
  } = useContentful()

  // Set State Vars
  const [state, setState] = useState('Active')
  const [remaining, setRemaining] = useState(timeout)
  const [open, setOpen] = useState(false)
  const [needsReset, setNeedsReset] = useState(false)

  const { resetSurveyActiveTimer } = useQualtricsSurvey()

  const onIdle = () => {
    setState('Idle')
    setNeedsReset(true)
    console.log('onIdle fired')
  }

  const onActive = () => {
    console.log('onActive fired')
    setState('Active')

    if (!needsReset) {
      resetSurveyActiveTimer((error) => {
        if (!error) {
          console.log(error)
        }
      })
      setOpen(false)
    }
  }

  const onPrompt = () => {
    console.log('onPrompt fired')
    setState('Prompted')
    setOpen(true)
  }

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  })

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000))
      // console.log('REMAINING TIME',getRemainingTime())
    }, 500)

    return () => {
      clearInterval(interval)
    }
  })

  const handleStillHere = () => {
    activate()
    setOpen(false)
  }

  const handleRestart = () => {
    window.location.reload(false)
  }

  const handleClose = () => {
    if (needsReset) {
      handleRestart()
    } else {
      handleStillHere()
    }
  }

  const timeTillPrompt = Math.max(remaining - promptBeforeIdle / 1000, 0)
  const seconds = timeTillPrompt > 1 ? 'seconds' : 'second'

  return (
    <div>
      {process.env.NODE_ENV == 'development' && (
        <>
          <Box
            sx={(theme) => ({
              width: '100%',
              height: '30px',
            })}
          ></Box>
          <Box
            sx={(theme) => ({
              position: 'fixed',
              top: '0',
              width: '100%',
              height: '30px',
              lineHeight: '30px',
              textAlign: 'center',
              backgroundColor: 'rgba(242,185,67,1)',
              zIndex: 1000,
            })}
          >
            Idle Modal opens in {convertSeconds(timeTillPrompt)}
          </Box>
        </>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div>
          <Collapse in={!needsReset}>
            <div>
              <DialogTitle
                id="alert-dialog-title"
                sx={{
                  textAlign: 'center',
                }}
              >
                {stillThereDialogTitle}
              </DialogTitle>
              <DialogContent
                sx={{
                  textAlign: 'center',
                }}
              >
                <DialogContentText id="alert-dialog-description">
                  <Typography
                    variant="body1"
                    component="span"
                    sx={(theme) => ({
                      color: theme.palette.text.primary,
                    })}
                  >
                    {stillThereMessage}
                  </Typography>
                  <br />
                  <Typography
                    variant="h4"
                    component="span"
                    sx={(theme) => ({
                      color: theme.palette.text.primary,
                      display: 'inline-block',
                    })}
                  >
                    {convertSeconds(remaining)}
                  </Typography>
                </DialogContentText>
              </DialogContent>
              <DialogActions
                sx={{
                  justifyContent: 'center',
                }}
              >
                <Button
                  className="still-here-button"
                  onClick={handleStillHere}
                  autoFocus
                >
                  {stillThereButtonText}
                </Button>
              </DialogActions>
            </div>
          </Collapse>

          <Collapse in={needsReset}>
            <div>
              {needsReset && <span className="needsReset"></span>}
              <DialogTitle
                id="alert-dialog-title"
                sx={{
                  textAlign: 'center',
                }}
              >
                {sessionExpiredDialogTitle}
              </DialogTitle>
              <DialogContent
                sx={{
                  textAlign: 'center',
                }}
              >
                <DialogContentText id="alert-dialog-description">
                  <Typography
                    variant="body1"
                    component="span"
                    sx={(theme) => ({
                      color: theme.palette.text.primary,
                    })}
                  >
                    {sessionExpiredMessage}
                  </Typography>
                </DialogContentText>
              </DialogContent>
              <DialogActions
                sx={{
                  justifyContent: 'center',
                }}
              >
                <Button onClick={handleRestart} autoFocus>
                  {sessionExpireButtonText}
                </Button>
              </DialogActions>
            </div>
          </Collapse>
        </div>
      </Dialog>
    </div>
  )
}

function convertSeconds(seconds) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  let result = ''
  if (hours > 0) {
    result += `${hours}h `
  }
  if (minutes > 0) {
    result += `${minutes}m `
  }
  result += `${remainingSeconds}s`

  return result
}
